import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const NewsletterManagement = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [schedulerStatus, setSchedulerStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [customMessage, setCustomMessage] = useState('');
    const [sendReport, setSendReport] = useState(null);

    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [subsRes, statusRes] = await Promise.all([
                axios.get(`${API_BASE}/admin/newsletter/subscribers`, { headers }),
                axios.get(`${API_BASE}/admin/newsletter/status`, { headers })
            ]);
            if (subsRes.data.success) setSubscribers(subsRes.data.data || []);
            if (statusRes.data.success) setSchedulerStatus(statusRes.data.data || statusRes.data);
        } catch (err) {
            toast.error('Failed to load newsletter data');
        } finally {
            setLoading(false);
        }
    };

    const handleSendNewsletter = async () => {
        try {
            setSending(true);
            const response = await axios.post(
                `${API_BASE}/admin/newsletter/send`,
                { customMessage },
                { headers }
            );
            if (response.data.success) {
                setSendReport(response.data.data || response.data);
                setShowSendModal(false);
                setCustomMessage('');
                toast.success('Newsletter sent successfully!');
                fetchData();
            } else {
                toast.error(response.data.message || 'Failed to send newsletter');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send newsletter');
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" style={{ color: '#6B8E23' }} role="status" />
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold mb-1">Newsletter Management</h3>
                    <p className="text-muted mb-0">Manage and send monthly newsletters to subscribed members</p>
                </div>
                <button
                    className="btn text-white fw-semibold px-4"
                    style={{ backgroundColor: '#6B8E23' }}
                    onClick={() => setShowSendModal(true)}
                >
                    <i className="bi bi-send me-2"></i>Send Newsletter Now
                </button>
            </div>

            {/* Stats Row */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: 50, height: 50, backgroundColor: '#e8f5e9' }}>
                                <i className="bi bi-people-fill fs-4" style={{ color: '#6B8E23' }}></i>
                            </div>
                            <div>
                                <div className="text-muted small">Total Subscribers</div>
                                <div className="fw-bold fs-4">{subscribers.length}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: 50, height: 50, backgroundColor: '#e3f2fd' }}>
                                <i className="bi bi-calendar-check fs-4 text-primary"></i>
                            </div>
                            <div>
                                <div className="text-muted small">Auto-Send Schedule</div>
                                <div className="fw-bold">1st of every month</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body d-flex align-items-center gap-3">
                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: 50, height: 50, backgroundColor: '#f3e5f5' }}>
                                <i className="bi bi-clock fs-4 text-purple" style={{ color: '#7b1fa2' }}></i>
                            </div>
                            <div>
                                <div className="text-muted small">Next Scheduled Send</div>
                                <div className="fw-bold small">{schedulerStatus?.nextRun || 'Loading...'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scheduler Status */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <span className={`badge ${schedulerStatus?.isActive ? 'bg-success' : 'bg-danger'}`}>
                            {schedulerStatus?.isActive ? '● Active' : '● Inactive'}
                        </span>
                        <span className="text-muted">Automatic Newsletter Scheduler</span>
                    </div>
                    <small className="text-muted">Timezone: UTC (9:00 AM)</small>
                </div>
            </div>

            {/* Send Report */}
            {sendReport && (
                <div className="alert alert-success border-0 shadow-sm mb-4">
                    <h6 className="fw-bold mb-2"><i className="bi bi-check-circle me-2"></i>Last Send Report</h6>
                    <div className="d-flex gap-4">
                        <span>Total: <strong>{sendReport.totalSubscribers || sendReport.total || 0}</strong></span>
                        <span className="text-success">Sent: <strong>{sendReport.sent || 0}</strong></span>
                        <span className="text-danger">Failed: <strong>{sendReport.failed || 0}</strong></span>
                    </div>
                </div>
            )}

            {/* Subscribers Table */}
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                    <h5 className="fw-bold mb-0">
                        <i className="bi bi-envelope-heart me-2" style={{ color: '#6B8E23' }}></i>
                        Newsletter Subscribers ({subscribers.length})
                    </h5>
                </div>
                <div className="card-body p-0">
                    {subscribers.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="bi bi-envelope-x fs-1 mb-3 d-block"></i>
                            No subscribers yet. Members opt in during registration.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Membership Type</th>
                                        <th>Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscribers.map((sub, idx) => (
                                        <tr key={sub.id || idx}>
                                            <td className="text-muted">{idx + 1}</td>
                                            <td className="fw-semibold">{sub.fullName || `${sub.firstName} ${sub.lastName}`}</td>
                                            <td>{sub.email}</td>
                                            <td>
                                                <span className="badge" style={{ backgroundColor: '#e8f5e9', color: '#6B8E23' }}>
                                                    {sub.membershipType || 'Member'}
                                                </span>
                                            </td>
                                            <td><span className="text-muted small">{sub.source || 'arca_members'}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Send Newsletter Modal */}
            {showSendModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header text-white" style={{ backgroundColor: '#6B8E23' }}>
                                <h5 className="modal-title fw-bold">
                                    <i className="bi bi-send me-2"></i>Send Newsletter Now
                                </h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowSendModal(false)} />
                            </div>
                            <div className="modal-body p-4">
                                <div className="alert alert-info border-0 mb-3">
                                    <i className="bi bi-info-circle me-2"></i>
                                    This will send the monthly newsletter to <strong>{subscribers.length} subscribers</strong> immediately.
                                    The newsletter will include your custom message plus the latest news, events, and announcements from the platform.
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Custom Message (optional)</label>
                                    <textarea
                                        className="form-control"
                                        rows={5}
                                        placeholder="Add a personal message to include in this newsletter (e.g., special announcements, greetings from leadership)..."
                                        value={customMessage}
                                        onChange={(e) => setCustomMessage(e.target.value)}
                                    />
                                    <small className="text-muted">Leave blank to send the default monthly newsletter template.</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowSendModal(false)}>Cancel</button>
                                <button
                                    className="btn text-white fw-semibold px-4"
                                    style={{ backgroundColor: '#6B8E23' }}
                                    onClick={handleSendNewsletter}
                                    disabled={sending}
                                >
                                    {sending ? (
                                        <><span className="spinner-border spinner-border-sm me-2" />Sending to {subscribers.length} members...</>
                                    ) : (
                                        <><i className="bi bi-send me-2"></i>Send to {subscribers.length} Members</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewsletterManagement;
